import Axios from "axios";

let aiBaseURL = (process.env.VUE_APP_API_URL || "https://api.arboran.com.br/api/")
  .replace(/\/api\/$/, "/ai-service/api/v1");
if (process.env.NODE_ENV === "development") {
  aiBaseURL = "http://localhost:8000/api/v1";
}

const publicHearingHttp = Axios.create({ baseURL: aiBaseURL });
const local = JSON.parse(localStorage.getItem("user-token") || "null");
if (local && local.token) {
  publicHearingHttp.defaults.headers.common["Authorization"] = "bearer " + local.token;
}
publicHearingHttp.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem("user-token");
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

const publicHttp = Axios.create({ baseURL: aiBaseURL });

class PublicHearingService {
  // ── Admin — Eventos ──────────────────────────────────────────────────────

  ListEvents(contaId) {
    return publicHearingHttp({ url: "/public-hearing/" + contaId, method: "GET" });
  }

  CreateEvent(contaId, data) {
    return publicHearingHttp({ url: "/public-hearing/" + contaId, data, method: "POST" });
  }

  GetEvent(eventId) {
    return publicHearingHttp({ url: "/public-hearing/events/" + eventId, method: "GET" });
  }

  UpdateEvent(eventId, data) {
    return publicHearingHttp({ url: "/public-hearing/events/" + eventId, data, method: "PUT" });
  }

  DeleteEvent(eventId) {
    return publicHearingHttp({ url: "/public-hearing/events/" + eventId, method: "DELETE" });
  }

  StartSpeechTimer(eventId, responseId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/speech-timer/start",
      data: { response_id: responseId },
      method: "PUT"
    });
  }

  StopSpeechTimer(eventId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/speech-timer/stop",
      method: "PUT"
    });
  }

  // ── Admin — Inscrições ───────────────────────────────────────────────────

  ListRegistrations(eventId, skip = 0, limit = 20, confirmedOnly = false) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/registrations",
      method: "GET",
      params: { skip, limit, confirmed_only: confirmedOnly }
    });
  }

  ConfirmRegistration(eventId, regId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/registrations/" + regId + "/confirm",
      method: "PUT"
    });
  }

  DeleteRegistration(eventId, regId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/registrations/" + regId,
      method: "DELETE"
    });
  }

  ExportRegistrations(eventId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/registrations/export",
      method: "GET",
      responseType: "blob"
    });
  }

  GetRegistrationsByNeighborhood(eventId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/registrations/by-neighborhood",
      method: "GET"
    });
  }

  // ── Admin — QR Code e Artwork ────────────────────────────────────────────

  GetQrCode(eventId, baseUrl, artworkType = "registration") {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/qrcode",
      method: "GET",
      params: { base_url: baseUrl, artwork_type: artworkType },
      responseType: "blob"
    });
  }

  GetDefaultPrompt(eventId, artworkType = "registration") {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/artwork/default-prompt",
      method: "GET",
      params: { artwork_type: artworkType }
    });
  }

  GenerateArtwork(eventId, prompt, quality, baseUrl, artworkType = "registration") {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/artwork/generate",
      data: { prompt: prompt || null, quality, base_url: baseUrl, artwork_type: artworkType },
      method: "POST"
    });
  }

  UploadArtwork(eventId, file, artworkType = "registration") {
    const formData = new FormData();
    formData.append("file", file);
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/artwork/upload",
      method: "POST",
      params: { artwork_type: artworkType },
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  ListArtworks(eventId, artworkType = null) {
    const params = artworkType ? { artwork_type: artworkType } : {};
    return publicHearingHttp({ url: "/public-hearing/events/" + eventId + "/artwork", method: "GET", params });
  }

  DeleteArtwork(eventId, artworkId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/artwork/" + artworkId,
      method: "DELETE"
    });
  }

  // ── Admin — Fotos ────────────────────────────────────────────────────────

  UploadPhoto(eventId, file, caption) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("caption", caption);
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/photos",
      method: "POST",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  ListPhotos(eventId) {
    return publicHearingHttp({ url: "/public-hearing/events/" + eventId + "/photos", method: "GET" });
  }

  UpdatePhoto(eventId, photoId, data) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/photos/" + photoId,
      data,
      method: "PUT"
    });
  }

  DeletePhoto(eventId, photoId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/photos/" + photoId,
      method: "DELETE"
    });
  }

  DescribePhoto(eventId, photoId) {
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/photos/" + photoId + "/describe",
      method: "POST"
    });
  }

  UploadPanelSnapshot(eventId, blob) {
    const formData = new FormData();
    formData.append("file", blob, "painel.png");
    return publicHearingHttp({
      url: "/public-hearing/events/" + eventId + "/panel-snapshot",
      method: "POST",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  // ── Público (sem auth) ───────────────────────────────────────────────────

  GetPanel(token) {
    return publicHttp({ url: "/public/public-hearing/" + token + "/panel", method: "GET" });
  }

  GetSuggestionsQrCode(token, baseUrl) {
    return publicHttp({
      url: "/public/public-hearing/" + token + "/suggestions-qrcode",
      method: "GET",
      params: { base_url: baseUrl },
      responseType: "blob"
    });
  }

  GetPublicEvent(token) {
    return publicHttp({ url: "/public/public-hearing/" + token, method: "GET" });
  }

  Register(token, data) {
    return publicHttp({ url: "/public/public-hearing/" + token + "/register", data, method: "POST" });
  }

  Confirm(token, identifier) {
    return publicHttp({
      url: "/public/public-hearing/" + token + "/confirm",
      data: { identifier },
      method: "POST"
    });
  }
}

export default new PublicHearingService();
